import React from 'react';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BUSINESS_TIMEZONE = 'Europe/Bucharest';
const timestampPattern = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,6})?)?(Z|[+-]\d{2}:?\d{2})?$/;
const timestampFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

// The Orders list supplies a Bucharest wall time without an offset. Preserve
// those fields directly; parsing that string as a Date uses the browser zone.
// Offset-bearing timestamps from the detail endpoint represent instants and
// must instead be converted to Bucharest before comparing calendar dates.
export function parseOrderDate(value) {
  if (typeof value !== 'string') return null;
  const match = timestampPattern.exec(value.trim());
  if (!match) return null;

  const [, year, month, day, hour, minute, second = '00', fraction = '', offset] = match;
  const fields = [year, month, day, hour, minute, second].map(Number);
  const [yearNumber, monthNumber, dayNumber, hourNumber, minuteNumber, secondNumber] = fields;
  if (yearNumber < 1 || monthNumber < 1 || monthNumber > 12
      || dayNumber < 1 || dayNumber > 31 || hourNumber > 23
      || minuteNumber > 59 || secondNumber > 59) return null;

  // Reject rolled-over dates such as February 30 before native Date parsing.
  const calendarDate = new Date(0);
  calendarDate.setUTCFullYear(yearNumber, monthNumber - 1, dayNumber);
  if (calendarDate.getUTCFullYear() !== yearNumber
      || calendarDate.getUTCMonth() !== monthNumber - 1
      || calendarDate.getUTCDate() !== dayNumber) return null;

  if (!offset) {
    return {
      dayKey: `${year}-${month}-${day}`,
      display: `${day}-${month}-${year} ${hour}:${minute}`
    };
  }

  const normalizedOffset = offset === 'Z' || offset.includes(':')
    ? offset
    : `${offset.slice(0, 3)}:${offset.slice(3)}`;
  const instant = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}${fraction}${normalizedOffset}`);
  if (!Number.isFinite(instant.getTime())) return null;

  const parts = timestampFormatter.formatToParts(instant)
    .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  const localYear = parts.year.padStart(4, '0');
  return {
    dayKey: `${localYear}-${parts.month}-${parts.day}`,
    display: `${parts.day}-${parts.month}-${localYear} ${parts.hour}:${parts.minute}`
  };
}

export function getOrderDateInfo(createdOn, deliveredOn) {
  const registered = parseOrderDate(createdOn);
  const delivery = parseOrderDate(deliveredOn);
  if (!registered || !delivery || registered.dayKey === delivery.dayKey) return null;
  return { registered, delivery };
}

export default function OrderDateInfo({ createdOn, deliveredOn, children }) {
  const dates = getOrderDateInfo(createdOn, deliveredOn);
  if (!dates) return children;

  const registeredLabel = `Created: ${dates.registered.display}`;
  const deliveryLabel = `Completed: ${dates.delivery.display}`;
  const dateHelp = 'Sales & stock use completion date.';

  return (
    <Tooltip
      arrow
      describeChild
      title={(
        <span>
          {registeredLabel}<br />
          {deliveryLabel}<br />
          {dateHelp}
        </span>
      )}
    >
      <span
        tabIndex={0}
        aria-label={`${registeredLabel}. ${deliveryLabel}. ${dateHelp}`}
        style={{ display: 'inline-block', position: 'relative' }}
      >
        {children}
        <InfoOutlinedIcon aria-hidden="true" sx={{ position: 'absolute', left: 'calc(100% + 4px)', top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'text.secondary' }} />
      </span>
    </Tooltip>
  );
}
